import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';

const RentalDurationForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        unitId: "",
        parkingSlotId: "",
        rentPeriodId: "",
        startDate: "",
        endDate: "",
        isActiveId: 1
    });

    const [unitList, setUnitList] = useState([]);
    const [parkingSlotList, setParkingSlotList] = useState([]);

    const isEdit = !!location.state?.recordData;
    const recordData = location.state?.recordData;

    useEffect(() => {
        if (isEdit && recordData) {
            const formatForInput = (dateStr) => {
                if (!dateStr) return "";
                const d = new Date(dateStr);
                const pad = (n) => n.toString().padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            };

            setFormData({
                unitId: recordData.UnitId || "",
                parkingSlotId: recordData.ParkingSlotId || "",
                rentPeriodId: recordData.RentPeriodId || "",
                startDate: formatForInput(recordData.StartDate),
                endDate: formatForInput(recordData.EndDate),
                isActiveId: recordData.IsActive === 'Active' ? 1 : 0
            });
            
            if (recordData.UnitId) {
                fetchParkingSlots(recordData.UnitId);
            }
        }
        fetchDropdownData();
    }, [isEdit, recordData]);

    const fetchDropdownData = async () => {
        try {
            const unitRes = await apiServices({ method: "GET", url: `/UnitMaster/GetAllUnitMaster` });
            if (unitRes.isSuccess || unitRes.success || unitRes.IsSuccess) {
                let apiData = unitRes.data;
                let data = [];
                if (Array.isArray(apiData)) data = apiData;
                else if (apiData && Array.isArray(apiData.data)) data = apiData.data;
                else if (apiData && Array.isArray(apiData.UnitMasterData)) data = apiData.UnitMasterData;
                
                setUnitList(data.map(u => ({label: `${u.UnitName} - ${u.UnitNumber}`, value: u.UnitId})));
            }
        } catch (error) {
            console.error("Error fetching dropdowns:", error);
            toast.error("Failed to load form options");
        }
    };

    const fetchParkingSlots = async (unitId) => {
        try {
            const res = await apiServices({ method: "GET", url: `/ParkingSlotMaster/getallParkingSlotId` });
            if (res.isSuccess || res.success || res.IsSuccess) {
                let apiData = res.data;
                let slots = [];
                if (Array.isArray(apiData)) slots = apiData;
                else if (apiData && Array.isArray(apiData.data)) slots = apiData.data;
                else if (apiData && Array.isArray(apiData.ParkingSlotData)) slots = apiData.ParkingSlotData;
                
                setParkingSlotList(slots.filter(item => item.unitId == unitId || item.UnitId == unitId).map(s => ({label: s.slotName, value: s.parkingSlotId || s.id})));
            }
        } catch (error) { console.error(error); }
    };

    const handleUnitChange = (opt) => {
        const unitId = opt ? opt.value : "";
        setFormData({ ...formData, unitId, parkingSlotId: "" });
        setParkingSlotList([]);
        if (unitId) fetchParkingSlots(unitId);
    };

    const validateForm = () => {
        if (!formData.unitId) return toast.warning("Unit is required"), false;
        if (!formData.parkingSlotId) return toast.warning("Parking Slot is required"), false;
        if (!formData.rentPeriodId) return toast.warning("Duration is required"), false;
        if (!formData.startDate) return toast.warning("Start Date is required"), false;
        if (!formData.endDate) return toast.warning("End Date is required"), false;
        if (formData.isActiveId === "") return toast.warning("Status is required"), false;
        
        if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
            return toast.warning("End Date cannot be before Start Date"), false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            parkingSlotId: parseInt(formData.parkingSlotId),
            unitId: parseInt(formData.unitId),
            rentPeriodId: parseInt(formData.rentPeriodId),
            startDate: formData.startDate,
            endDate: formData.endDate,
            createdBy: 'sys',
            isActiveId: parseInt(formData.isActiveId),
        };

        try {
            const res = await apiServices({
                method: "POST",
                url: `/RentParkingSlot/AddParkingSlotUnderUnitForRent`,
                data: payload
            });

            if (res.isSuccess || res.success || res.IsSuccess) {
                toast.success(`Parking assigned for rent successfully`);
                navigate('/zentcomm/parking-management/rent-Type');
            } else {
                toast.error(res.message || `Failed to assign parking for rent`);
            }
        } catch (error) {
            toast.error("An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <PageHeadingSection 
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Rental Duration`}
                subHeading={`Please fill out the form below to set up rental periods for parking slots.`}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect 
                    label={<>Unit <span className="text-red-500">*</span></>}
                    options={unitList}
                    value={unitList.find(u => u.value === formData.unitId) || null}
                    onChange={handleUnitChange}
                    isDisabled={isEdit}
                />
                
                <CustomSelect 
                    label={<>Parking Slot <span className="text-red-500">*</span></>}
                    options={parkingSlotList}
                    value={parkingSlotList.find(s => s.value === formData.parkingSlotId) || null}
                    onChange={(opt) => setFormData({ ...formData, parkingSlotId: opt ? opt.value : "" })}
                    isDisabled={!formData.unitId || isEdit}
                />
                

                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="startDate">
                        Start Date <span className="text-red-500">*</span>
                    </label>
                    <input 
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors" 
                        type="datetime-local" 
                        id="startDate" 
                        value={formData.startDate} 
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    />
                </div>
                
                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="endDate">
                        End Date <span className="text-red-500">*</span>
                    </label>
                    <input 
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors" 
                        type="datetime-local" 
                        id="endDate" 
                        value={formData.endDate} 
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    />
                </div>
                
                <CustomSelect 
                    label={<>Duration <span className="text-red-500">*</span></>}
                    options={[
                        {label: 'Daily', value: '1'},
                        {label: 'Weekly', value: '2'},
                        {label: 'Monthly', value: '3'},
                        {label: 'Yearly', value: '4'}
                    ]}
                    value={['1','2','3','4'].includes(formData.rentPeriodId.toString()) ? {label: ['Daily','Weekly','Monthly','Yearly'][parseInt(formData.rentPeriodId)-1], value: formData.rentPeriodId.toString()} : null}
                    onChange={(opt) => setFormData({ ...formData, rentPeriodId: opt ? opt.value : "" })}
                />
                
                <CustomSelect 
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{label: 'Active', value: 1}, {label: 'Inactive', value: 0}]}
                    value={formData.isActiveId !== "" ? {label: formData.isActiveId === 1 ? 'Active' : 'Inactive', value: formData.isActiveId} : null}
                    onChange={(opt) => setFormData({ ...formData, isActiveId: opt ? opt.value : "" })}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentcomm/parking-management/rent-Type")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default RentalDurationForm;
